import React, { useState } from "react";
import { MapPin, Phone, Mail, Clock, Car, Loader2, Instagram, Navigation } from "lucide-react";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { BRAND, HOURS } from "@/lib/content";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent! We'll be in touch.");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  const field = "w-full bg-neutral-100 border border-black/12 rounded-xl px-4 py-3 text-neutral-900 placeholder:text-neutral-300 outline-none focus:border-neutral-900 transition-colors";
  const mapSrc = `https://www.google.com/maps?q=${encodeURIComponent(BRAND.mapsQuery)}&output=embed`;

  return (
    <>
      <Seo title="Contact & Directions" description="Visit ChiCha Lounge at 1214 U St NW, Washington, DC. Hours, parking, directions and contact info for DC's premier hookah lounge." path="/contact" />
      <PageHero overline="Find Us" title="Visit ChiCha" subtitle="In the heart of U Street. Reservations, questions or private events — we're here." />

      <section className="pb-28 bg-transparent">
        <div className="max-w-[1400px] mx-auto px-5 md:px-8 grid lg:grid-cols-2 gap-12">
          <div className="space-y-6">
            <Reveal>
              <div className="rounded-2xl overflow-hidden border border-black/10 h-[320px]">
                <iframe title="ChiCha Lounge Map" src={mapSrc} className="w-full h-full" style={{ border: 0, filter: "grayscale(0.3) invert(0.9) hue-rotate(180deg)" }} loading="lazy" />
              </div>
            </Reveal>
            <div className="grid sm:grid-cols-2 gap-4">
              <Reveal className="card-lux p-6">
                <MapPin className="text-neutral-900 mb-3" size={20} />
                <h4 className="font-serif text-xl text-neutral-900">Location</h4>
                <p className="text-neutral-500 text-sm mt-1">{BRAND.address}</p>
                <a href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(BRAND.mapsQuery)}`} target="_blank" rel="noreferrer" data-testid="directions-link" className="inline-flex items-center gap-1 text-neutral-900 text-sm mt-3"><Navigation size={14} /> Directions</a>
              </Reveal>
              <Reveal delay={0.1} className="card-lux p-6">
                <Phone className="text-neutral-900 mb-3" size={20} />
                <h4 className="font-serif text-xl text-neutral-900">Contact</h4>
                <a href={`tel:${BRAND.phoneRaw}`} className="block text-neutral-500 text-sm mt-1 hover:text-neutral-900">{BRAND.phone}</a>
                <a href={`mailto:${BRAND.email}`} className="block text-neutral-500 text-sm hover:text-neutral-900">{BRAND.email}</a>
                <a href={BRAND.instagram} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-neutral-900 text-sm mt-2"><Instagram size={14} /> @chichalounge</a>
              </Reveal>
              <Reveal delay={0.15} className="card-lux p-6">
                <Car className="text-neutral-900 mb-3" size={20} />
                <h4 className="font-serif text-xl text-neutral-900">Parking</h4>
                <p className="text-neutral-500 text-sm mt-1">Street parking on U St & 13th. Paid garages nearby. Metro: U Street/African-Amer Civil War Memorial (Green/Yellow).</p>
              </Reveal>
              <Reveal delay={0.2} className="card-lux p-6">
                <Clock className="text-neutral-900 mb-3" size={20} />
                <h4 className="font-serif text-xl text-neutral-900">Hours</h4>
                <ul className="mt-1 space-y-0.5">
                  {HOURS.slice(0, 4).map((h) => <li key={h.day} className="flex justify-between text-xs text-neutral-500"><span>{h.day}</span><span>{h.time}</span></li>)}
                  <li className="text-xs text-neutral-400 pt-1">Weekends open at 2PM</li>
                </ul>
              </Reveal>
            </div>
          </div>

          <Reveal delay={0.1}>
            <form onSubmit={submit} className="card-lux p-8 space-y-5">
              <h2 className="font-serif text-3xl text-neutral-900">Send a Message</h2>
              <div><label className="overline block mb-2">Name</label><input data-testid="contact-name" required value={form.name} onChange={(e) => set("name", e.target.value)} className={field} placeholder="Your name" /></div>
              <div><label className="overline block mb-2">Email</label><input data-testid="contact-email" required type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className={field} placeholder="you@email.com" /></div>
              <div><label className="overline block mb-2">Message</label><textarea data-testid="contact-message" required rows="6" value={form.message} onChange={(e) => set("message", e.target.value)} className={field} placeholder="How can we help?" /></div>
              <button data-testid="contact-submit" disabled={loading} className="btn-gold w-full justify-center">{loading ? <Loader2 className="animate-spin" size={18} /> : null} {loading ? "Sending…" : "Send Message"}</button>
            </form>
          </Reveal>
        </div>
      </section>
    </>
  );
}
